import * as api from '../../api'
import router from '../../router'

function rowPick (row, ...names) {
  if (!row || typeof row !== 'object') return undefined
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(row, name)) return row[name]
  }
  const lower = {}
  for (const k of Object.keys(row)) {
    lower[String(k).toLowerCase()] = row[k]
  }
  for (const name of names) {
    const v = lower[String(name).toLowerCase()]
    if (v !== undefined) return v
  }
  return undefined
}

function sqlLikeTimestamp (d) {
  const x = d instanceof Date ? d : new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())} ${p(x.getHours())}:${p(x.getMinutes())}:${p(x.getSeconds())}`
}

function toSqlLikeTimestampOrFallback (v, fallback) {
  if (v === undefined || v === null || v === '') return fallback
  if (v instanceof Date) return sqlLikeTimestamp(v)
  const s = String(v)
  // Normalize ISO form "2026-03-30T20:00:00.000Z" to SQL-like form.
  if (s.indexOf('T') >= 0) {
    const d = new Date(s)
    if (!Number.isNaN(d.getTime())) return sqlLikeTimestamp(d)
  }
  return s
}

/**
 * POST /doc/ expects legacy shape: class_* + subdoc_list + rid + records[] (slim rows).
 * RESTFUL_DOCSEARCH returns catalog fields merged into each row — re-shape for create flow.
 */
function applyCreateDocVisitContext (envelope, { rid, visitId }) {
  if (!envelope || !Array.isArray(envelope.records)) return envelope
  const hasVisit = visitId !== undefined && visitId !== null && visitId !== ''
  const hasRid = rid !== undefined && rid !== null && rid !== ''
  if (!hasVisit && !hasRid) return envelope
  for (let i = 0; i < envelope.records.length; i++) {
    const rec = envelope.records[i]
    if (!rec || typeof rec !== 'object') continue
    if (hasVisit) rec.visit_id = visitId
    if (hasRid) rec.rid = rid
  }
  return envelope
}

function buildLegacyTemplateDocEnvelope (rawEnvelope, { rid, visitId }) {
  if (!rawEnvelope || !Array.isArray(rawEnvelope.records) || rawEnvelope.records.length === 0) {
    return rawEnvelope
  }

  const R0 = rawEnvelope.records[0]
  const classId = rowPick(rawEnvelope, 'class_id', 'Class_Id', 'classId') ?? rowPick(R0, 'class_id', 'Class_Id', 'classId')
  const subclassId = rowPick(R0, 'subclass_id', 'Subclass_Id', 'subclassId')
  const nowStr = sqlLikeTimestamp(new Date())
  const resolvedRid = (rid !== undefined && rid !== null && rid !== '')
    ? rid
    : (rowPick(rawEnvelope, 'rid', 'Rid') ?? rowPick(R0, 'rid', 'Rid'))
  const resolvedVisitId = (visitId !== undefined && visitId !== null && visitId !== '')
    ? visitId
    : rowPick(R0, 'visit_id', 'Visit_Id', 'visitId')

  const subdocEntry = {
    subclass_id: subclassId,
    class_id: classId,
    subclass_name: rowPick(R0, 'subclass_name', 'Subclass_Name') ?? '',
    subclass_detail: rowPick(R0, 'subclass_detail', 'Subclass_Detail') ?? '',
    page_template: '',
    page_sql: ''
  }

  const slimRecords = rawEnvelope.records.map((R) => {
    if (!R || typeof R !== 'object') return R
    return {
      rec_id: rowPick(R, 'rec_id', 'Rec_Id', 'recId') ?? 0,
      rid: resolvedRid,
      visit_id: resolvedVisitId,
      subclass_id: rowPick(R, 'subclass_id', 'Subclass_Id', 'subclassId'),
      subclass_name: rowPick(R, 'subclass_name', 'Subclass_Name') ?? '',
      page_template: rowPick(R, 'page_template', 'Page_Template') ?? '',
      page_sql: rowPick(R, 'page_sql', 'Page_Sql') ?? '',
      page_ele: Array.isArray(R.page_ele) ? R.page_ele : [],
      page_html: rowPick(R, 'page_html', 'Page_Html') ?? '',
      rec_state: rowPick(R, 'rec_state', 'Rec_State') ?? 0,
      user_id: rowPick(R, 'user_id', 'User_Id') ?? 0,
      entry_dt: toSqlLikeTimestampOrFallback(rowPick(R, 'entry_dt', 'Entry_Dt'), nowStr),
      lastupdate: toSqlLikeTimestampOrFallback(rowPick(R, 'lastupdate', 'LastUpdate', 'last_update'), nowStr)
    }
  })

  const rawSubdoc = Array.isArray(rawEnvelope.subdoc_list) && rawEnvelope.subdoc_list.length > 0
    ? rawEnvelope.subdoc_list[0]
    : null
  const resolvedSubdoc = {
    subclass_id: rowPick(rawSubdoc, 'subclass_id', 'Subclass_Id', 'subclassId') ?? subdocEntry.subclass_id,
    class_id: rowPick(rawSubdoc, 'class_id', 'Class_Id', 'classId') ?? subdocEntry.class_id,
    subclass_name: rowPick(rawSubdoc, 'subclass_name', 'Subclass_Name') ?? subdocEntry.subclass_name,
    subclass_detail: rowPick(rawSubdoc, 'subclass_detail', 'Subclass_Detail') ?? subdocEntry.subclass_detail,
    page_template: rowPick(rawSubdoc, 'page_template', 'Page_Template') ?? '',
    page_sql: rowPick(rawSubdoc, 'page_sql', 'Page_Sql') ?? ''
  }

  return {
    class_id: classId,
    class_name: rowPick(rawEnvelope, 'class_name', 'Class_Name') ?? rowPick(R0, 'class_name', 'Class_Name') ?? '',
    class_detail: rowPick(rawEnvelope, 'class_detail', 'Class_Detail') ?? rowPick(R0, 'class_detail', 'Class_Detail') ?? '',
    subdoc_list: [resolvedSubdoc],
    rid: resolvedRid,
    records: slimRecords
  }
}

function isLegacyPageElePair (e) {
  return e != null && typeof e === 'object' && !Array.isArray(e) && e.ele_name != null
}

function isRawSqlPageEleRow (e) {
  return (
    e != null &&
    typeof e === 'object' &&
    !Array.isArray(e) &&
    !isLegacyPageElePair(e) &&
    (Object.prototype.hasOwnProperty.call(e, 'lb001') ||
      Object.prototype.hasOwnProperty.call(e, 'lr11'))
  )
}

/** medcert2.vue: SQL uses lb01, tx09, tx10, tr11; medcert1/3/4 share lb01 but not tr11 */
function isMedcert2SqlPageEleRow (e) {
  return (
    e != null &&
    typeof e === 'object' &&
    !Array.isArray(e) &&
    !isLegacyPageElePair(e) &&
    Object.prototype.hasOwnProperty.call(e, 'lb01') &&
    !Object.prototype.hasOwnProperty.call(e, 'lb001') &&
    Object.prototype.hasOwnProperty.call(e, 'tr11')
  )
}

function cellStr (v) {
  if (v === null || v === undefined) return ''
  return String(v)
}

/**
 * deferdebit.vue expects v-bind keys: lb001–lb018, lb019–lb024, lr11_1…lr17_28, lr12_1…lr18_28
 * from page_sql row shape (lb001, lr11, …) — not { ele_name, ele_val }.
 */
function expandDeferDebitSqlRowsToPageAttrs (rows) {
  const out = {}
  if (!Array.isArray(rows) || rows.length === 0) return out
  const first = rows[0]
  for (let k = 1; k <= 18; k++) {
    const key = 'lb' + String(k).padStart(3, '0')
    if (Object.prototype.hasOwnProperty.call(first, key)) {
      out[key] = cellStr(first[key])
    }
  }
  if (Object.prototype.hasOwnProperty.call(first, 'lb019_nt') || Object.prototype.hasOwnProperty.call(first, 'lb019')) {
    out.lb019 = cellStr(first.lb019_nt != null ? first.lb019_nt : first.lb019)
  }
  if (Object.prototype.hasOwnProperty.call(first, 'lb020_nt') || Object.prototype.hasOwnProperty.call(first, 'lb020')) {
    out.lb020 = cellStr(first.lb020_nt != null ? first.lb020_nt : first.lb020)
  }
  ;['lb021', 'lb022', 'lb023', 'lb024'].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(first, key)) out[key] = cellStr(first[key])
  })
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const n = i + 1
    out['lr11_' + n] = cellStr(r.lr11)
    out['lr12_' + n] = cellStr(r.lr12)
    out['lr13_' + n] = cellStr(r.lr13)
    out['lr14_' + n] = cellStr(r.lr14)
    out['lr15_' + n] = cellStr(r.lr15)
    out['lr16_' + n] = cellStr(r.lr16)
    out['lr17_' + n] = cellStr(r.lr16)
    out['lr18_' + n] = cellStr(r.lr16)
  }
  return out
}

/**
 * Multi-row defer debit: document API concatenates each SQL row into one page_ele array (duplicate ele_names).
 * One logical row ends with lb024 after lr11…lr16 and totals.
 */
function tryParseDeferDebitLegacyPageEleRows (pageEle) {
  if (!Array.isArray(pageEle) || pageEle.length === 0) return null
  const rows = []
  let current = {}
  for (let i = 0; i < pageEle.length; i++) {
    const e = pageEle[i]
    if (!isLegacyPageElePair(e)) return null
    current[e.ele_name] = (e.ele_val === null || e.ele_val === undefined) ? '' : e.ele_val
    if (e.ele_name === 'lb024') {
      if (!Object.prototype.hasOwnProperty.call(current, 'lr11')) return null
      rows.push(current)
      current = {}
    }
  }
  if (Object.keys(current).length > 0) return null
  return rows.length > 0 ? rows : null
}

/**
 * appoint_card: หลายนัดต่อ patient — API ต่อแถวเป็น lb001…lb003, lr01, lr02 แล้ววนซ้ำ
 */
function tryParseAppointCardLegacyPageEleRows (pageEle) {
  if (!Array.isArray(pageEle) || pageEle.length === 0) return null
  const rows = []
  let current = {}
  for (let i = 0; i < pageEle.length; i++) {
    const e = pageEle[i]
    if (!isLegacyPageElePair(e)) return null
    current[e.ele_name] = (e.ele_val == null || e.ele_val === undefined) ? '' : e.ele_val
    if (e.ele_name === 'lr02') {
      if (!Object.prototype.hasOwnProperty.call(current, 'lb001')) return null
      rows.push(current)
      current = {}
    }
  }
  if (Object.keys(current).length > 0) return null
  return rows.length > 0 ? rows : null
}

/** appointcard.vue: สูงสุด 16 คู่ lr01_n / lr02_n */
function expandAppointCardRowsToPageAttrs (rows) {
  const out = {}
  if (!Array.isArray(rows) || rows.length === 0) return out
  const first = rows[0]
  ;['lb001', 'lb002', 'lb003'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(first, k)) out[k] = cellStr(first[k])
  })
  const max = Math.min(rows.length, 16)
  for (let i = 0; i < max; i++) {
    const r = rows[i]
    const n = i + 1
    out['lr01_' + n] = cellStr(r.lr01)
    out['lr02_' + n] = cellStr(r.lr02)
  }
  for (let j = max + 1; j <= 16; j++) {
    out['lr01_' + j] = ''
    out['lr02_' + j] = ''
  }
  return out
}

/**
 * REST/document API flattens SQL rows to [{ ele_name, ele_val }, …] with lr11…lr18 (no _row suffix).
 * deferdebit.vue binds lr11_1…lr18_1 etc. — mirror expandDeferDebitSqlRowsToPageAttrs for row 1.
 */
function expandDeferDebitLegacyEleMapIfNeeded (m) {
  if (!m || typeof m !== 'object') return m
  const lineKeys = ['lr11', 'lr12', 'lr13', 'lr14', 'lr15', 'lr16', 'lr17', 'lr18']
  const hasLineCol = lineKeys.some((k) => Object.prototype.hasOwnProperty.call(m, k))
  if (!hasLineCol) return m
  if (Object.prototype.hasOwnProperty.call(m, 'lr11_1')) return m
  const out = { ...m }
  const n = 1
  const cell = (k) => (Object.prototype.hasOwnProperty.call(m, k) ? cellStr(m[k]) : '')
  out['lr11_' + n] = cell('lr11')
  out['lr12_' + n] = cell('lr12')
  out['lr13_' + n] = cell('lr13')
  out['lr14_' + n] = cell('lr14')
  out['lr15_' + n] = cell('lr15')
  out['lr16_' + n] = cell('lr16')
  out['lr17_' + n] = cell('lr16')
  out['lr18_' + n] = cell('lr16')
  return out
}

function expandMedcert2SqlRowsToPageAttrs (rows) {
  const out = {}
  if (!Array.isArray(rows) || rows.length === 0) return out
  const first = rows[0]
  ;['lb01', 'lb02', 'lb03', 'lb04', 'lb05', 'lb06', 'lb07'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(first, k)) out[k] = cellStr(first[k])
  })
  if (Object.prototype.hasOwnProperty.call(first, 'tx08')) out.tx08 = cellStr(first.tx08)
  if (Object.prototype.hasOwnProperty.call(first, 'tx09')) out.lb09 = cellStr(first.tx09)
  else if (Object.prototype.hasOwnProperty.call(first, 'lb09')) out.lb09 = cellStr(first.lb09)
  if (Object.prototype.hasOwnProperty.call(first, 'tx10')) out.lb10 = cellStr(first.tx10)
  else if (Object.prototype.hasOwnProperty.call(first, 'lb10')) out.lb10 = cellStr(first.lb10)
  ;['tx12', 'tx13', 'tx14', 'lb17', 'lb18'].forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(first, k)) out[k] = cellStr(first[k])
  })
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const n = i + 1
    out['tr11_' + n] = Object.prototype.hasOwnProperty.call(r, 'tr11') ? cellStr(r.tr11) : ''
  }
  for (let j = rows.length + 1; j <= 5; j++) {
    out['tr11_' + j] = ''
  }
  return out
}

/** Flatten raw SQL row objects to page attrs (medcert1/3/4, echo, refer, mamo, radious, …). */
function isFlattenableSqlPageEleRow (e) {
  if (e == null || typeof e !== 'object' || Array.isArray(e) || isLegacyPageElePair(e)) return false
  if (isMedcert2SqlPageEleRow(e) || isRawSqlPageEleRow(e)) return false
  return true
}

function expandFlattenSqlRowsToPageAttrs (rows) {
  const out = {}
  if (!Array.isArray(rows) || rows.length === 0) return out
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    if (!r || typeof r !== 'object') continue
    for (const k of Object.keys(r)) {
      out[k] = cellStr(r[k])
    }
  }
  return out
}

/**
 * Legacy `page_html` pipe form: "lb01`value|lb02`v2|ck01`true|..."
 * ไม่เคยถูกอ่านใน UI เดิม — ผสานเป็น docData.page เพื่อเติมคีย์ที่ไม่มีใน page_ele
 */
function pageHtmlStringToAttrMap (pageHtml) {
  const out = {}
  if (pageHtml == null || typeof pageHtml !== 'string' || pageHtml.trim() === '') return out
  const parts = pageHtml.split('|')
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    if (!p) continue
    const tick = p.indexOf('`')
    if (tick <= 0) continue
    const key = p.slice(0, tick).trim()
    if (!key) continue
    out[key] = p.slice(tick + 1)
  }
  return out
}

function buildPageHtmlFromPageEle (pageEle) {
  if (!Array.isArray(pageEle) || pageEle.length === 0) return ''
  const parts = []
  for (let i = 0; i < pageEle.length; i++) {
    const e = pageEle[i]
    if (!isLegacyPageElePair(e)) continue
    const key = (e.ele_name == null) ? '' : String(e.ele_name).trim()
    if (key === '') continue
    parts.push(`${key}\`${cellStr(e.ele_val)}`)
  }
  return parts.join('|')
}

function collectOrderedTr11FromLegacyPageEle (pageEle) {
  if (!Array.isArray(pageEle) || pageEle.length === 0) return null
  const trs = []
  for (let i = 0; i < pageEle.length; i++) {
    const e = pageEle[i]
    if (!isLegacyPageElePair(e)) continue
    if (e.ele_name === 'tr11') {
      trs.push((e.ele_val == null || e.ele_val === undefined) ? '' : String(e.ele_val))
    }
  }
  return trs.length > 0 ? trs : null
}

/**
 * medcert2.vue ใช้ ref tr11_1…tr11_5 — page_ele / page_html มักส่งแค่ tr11 หรือหลายแถว tr11 ต่อกัน
 */
function expandMedcert2Tr11OnFlatMap (m, pageEle) {
  if (!m || typeof m !== 'object') return m
  const trList = pageEle ? collectOrderedTr11FromLegacyPageEle(pageEle) : null
  if (trList && trList.length > 0) {
    const out = { ...m }
    for (let i = 0; i < 5; i++) {
      out['tr11_' + (i + 1)] = i < trList.length ? trList[i] : ''
    }
    return out
  }
  const hasBareTr11 = Object.prototype.hasOwnProperty.call(m, 'tr11')
  const hasIdx = [1, 2, 3, 4, 5].some((j) =>
    Object.prototype.hasOwnProperty.call(m, 'tr11_' + j)
  )
  if (!hasBareTr11 && !hasIdx) return m
  const out = { ...m }
  if (hasBareTr11 && !Object.prototype.hasOwnProperty.call(out, 'tr11_1')) {
    out.tr11_1 = cellStr(out.tr11)
  }
  for (let j = 1; j <= 5; j++) {
    const k = 'tr11_' + j
    if (!Object.prototype.hasOwnProperty.call(out, k)) out[k] = ''
  }
  return out
}

function pageEleArrayToPageMap (pageEle) {
  if (!Array.isArray(pageEle) || pageEle.length === 0) return {}
  if (pageEle.every(isLegacyPageElePair)) {
    const deferRows = tryParseDeferDebitLegacyPageEleRows(pageEle)
    if (deferRows != null && deferRows.length > 0) {
      return expandDeferDebitSqlRowsToPageAttrs(deferRows)
    }
    const appointRows = tryParseAppointCardLegacyPageEleRows(pageEle)
    if (appointRows != null && appointRows.length > 0) {
      return expandAppointCardRowsToPageAttrs(appointRows)
    }
    const jsonformat = {}
    for (let i = 0; i < pageEle.length; i++) {
      const e = pageEle[i]
      jsonformat[e.ele_name] = (e.ele_val === null || e.ele_val === undefined) ? '' : e.ele_val
    }
    return expandDeferDebitLegacyEleMapIfNeeded(jsonformat)
  }
  if (pageEle.every(isMedcert2SqlPageEleRow)) {
    return expandMedcert2SqlRowsToPageAttrs(pageEle)
  }
  if (pageEle.every(isRawSqlPageEleRow)) {
    return expandDeferDebitSqlRowsToPageAttrs(pageEle)
  }
  if (pageEle.every(isFlattenableSqlPageEleRow)) {
    return expandFlattenSqlRowsToPageAttrs(pageEle)
  }
  const legacyOnly = pageEle.filter(isLegacyPageElePair)
  if (legacyOnly.length === pageEle.length) {
    const deferRows = tryParseDeferDebitLegacyPageEleRows(legacyOnly)
    if (deferRows != null && deferRows.length > 0) {
      return expandDeferDebitSqlRowsToPageAttrs(deferRows)
    }
    const appointRows = tryParseAppointCardLegacyPageEleRows(legacyOnly)
    if (appointRows != null && appointRows.length > 0) {
      return expandAppointCardRowsToPageAttrs(appointRows)
    }
  }
  const jsonformat = {}
  for (let i = 0; i < pageEle.length; i++) {
    const e = pageEle[i]
    if (isLegacyPageElePair(e)) {
      jsonformat[e.ele_name] = (e.ele_val === null || e.ele_val === undefined) ? '' : e.ele_val
    }
  }
  if (Object.keys(jsonformat).length > 0) return expandDeferDebitLegacyEleMapIfNeeded(jsonformat)
  if (isMedcert2SqlPageEleRow(pageEle[0])) return expandMedcert2SqlRowsToPageAttrs(pageEle)
  if (isRawSqlPageEleRow(pageEle[0])) return expandDeferDebitSqlRowsToPageAttrs(pageEle)
  if (isFlattenableSqlPageEleRow(pageEle[0])) return expandFlattenSqlRowsToPageAttrs(pageEle)
  return {}
}

const getDefaultState = () => {
  return {
    docList: { data: [], loading: false, status: null },
    docType: { data: [], loading: false, status: null },
    docData: { data: [], loading: false, status: null, post: null, page: {} },
    myDocuments: []
  }
}
const state = getDefaultState()

const mutations = {
  resetstate (state) {
    Object.assign(state, getDefaultState())
  },
  setDocList (state, data) {
    const normalizedData = (data && Array.isArray(data.records))
      ? data
      : { records: [] }

    normalizedData.records.forEach((element) => {
      element.loading = false
      element.disabled = false
    })
    state.docList.data = normalizedData
  },
  setDocListLoading (state, data) {
    state.docList.loading = data
  },
  setDocListStatus (state, data) {
    state.docList.status = data
  },
  setDocType (state, data) {
    state.docType.data = data
  },
  setDocTypeLoading (state, data) {
    state.docType.loading = data
  },
  setDocTypeStatus (state, data) {
    state.docType.status = data
  },
  setDocData (state, data) {
    state.docData.data = data
    if (data == null || (Object.entries(data).length === 0 && data.constructor === Object)) {
      state.docData.page = {}
      return
    }
    let pageEle = null
    let rec = null
    if (Array.isArray(data.records) && data.records.length > 0) {
      const withPageEle = data.records.find(
        (r) => r && Array.isArray(r.page_ele) && r.page_ele.length > 0
      )
      rec = withPageEle || data.records[0]
      if (rec && Array.isArray(rec.page_ele)) {
        pageEle = rec.page_ele
      }
    } else if (Array.isArray(data.page_ele)) {
      pageEle = data.page_ele
    }
    let pageMap = {}
    if (rec && typeof rec.page_html === 'string' && rec.page_html.trim() !== '') {
      pageMap = pageHtmlStringToAttrMap(rec.page_html)
    }
    if (pageEle && pageEle.length > 0) {
      pageMap = { ...pageMap, ...pageEleArrayToPageMap(pageEle) }
    }
    state.docData.page = expandMedcert2Tr11OnFlatMap(pageMap, pageEle)
  },
  setDocDataLoading (state, data) {
    state.docData.loading = data
  },
  setDocDataStatus (state, data) {
    state.docData.status = data
  },
  setDocDataPost (state, data) {
    state.docData.post = data
  },
  SET_MY_DOCUMENT (state, { item, rid }) {
    item = (item) ? item : []
    item.forEach(element => {
      element.loading = false
      element.disable = false
      element.rid = rid
    })
    state.myDocuments = (item) ? item : []
  },
  SET_MY_DOCUMENT_LOADING (state, { item, loading }) {
    state.myDocuments[state.myDocuments.indexOf(item)].loading = loading
  }
}

const actions = {
  ResetBed ({ commit }) {
    commit('resetstate')
  },
  GetDocType ({ rootState, commit }) {
    commit('setDocTypeLoading', true)
    commit('setDocTypeStatus', null)
    commit('setDocType', [])
    api.GetDocByRid({ rid: 0, rec: 0, groupId: rootState.User.group.group_id }, message => {
      if (message.data !== null && message.data !== undefined) {
        commit('setDocTypeStatus', true)
        let catalog = message.data
        if (!Array.isArray(catalog) && catalog && Array.isArray(catalog.records)) {
          catalog = catalog.records
        }
        commit('setDocType', Array.isArray(catalog) ? catalog : [])
      } else {
        commit('setDocTypeStatus', false)
      }
      commit('setDocTypeLoading', false)
    }, error => {
      console.log(error)
      commit('setDocTypeStatus', false)
      commit('setDocTypeLoading', false)
      if (error.response.status === 401) {

      }
    })
  },
  GetDocList ({ rootState, commit }, { rid, rec }) {
    commit('setDocListLoading', true)
    api.GetDocByRid({ rid: rid, rec: rec, groupId: rootState.User.group.group_id }, message => {
      if (message.data !== null) {
        commit('setDocListStatus', true)
        commit('setDocList', message.data[0])
      } else {
        commit('setDocList', { records: [] })
        commit('setDocListStatus', false)
      }
      commit('setDocListLoading', false)
    }, error => {
      console.log(error)
      commit('setDocList', { records: [] })
      commit('setDocListStatus', false)
      commit('setDocListLoading', false)
    })
  },
  GET_DOC_TEMPLATE_WITH_DATA ({ rootState, commit }, item) {
    commit('SET_MY_DOCUMENT_LOADING', { item: item, loading: true })
    return new Promise((resolve, reject) => {
      api.GetDocByRid({ rid: item.rid, rec: item.rec_id, groupId: rootState.User.group.group_id }, message => {
        commit('SET_MY_DOCUMENT_LOADING', { item: item, loading: false })
        if (message.data) {
          commit('setDocData', message.data[0])
          resolve()
        } else {
          reject(new Error('Null data'))
        }
      }, error => {
        commit('SET_MY_DOCUMENT_LOADING', { item: item, loading: false })
        reject(new Error(error))
      })
    })
  },
  GET_DOC_LIST_BY_RID ({ rootState, commit }, data) {
    return new Promise((resolve, reject) => {
      api.GetDocByRid({ rid: data.rid, rec: 0, groupId: rootState.User.group.group_id }, message => {
        if (message.data) {
          commit('SET_MY_DOCUMENT', { item: (message.data) ? message.data[0].records : [] , rid: data.rid })
          resolve()
        } else {
          commit('SET_MY_DOCUMENT', { item: [] , rid: data.rid })
          resolve()
        }
      }, error => {
        reject(new Error(error))
      })
    })
  },
  GetDocData ({ rootState, commit }, { rid, rec }) {
    commit('setDocDataLoading', true)
    commit('setDocDataStatus', null)
    // commit('setDocData', {})
    api.GetDocByRid({ rid: rid, rec: rec, groupId: rootState.User.group.group_id }, message => {
      if (message.data) {
        commit('setDocDataStatus', true)
        commit('setDocData', message.data[0])
      } else {
         commit('setDocData',{})
        commit('setDocDataStatus', false)
      }
      commit('setDocDataLoading', false)
    }, error => {
      console.log(error)
      commit('setDocDataStatus', false)
      commit('setDocDataLoading', false)
    })
  },
  GetDocTemplateWithData ({ rootState, commit }, { rid, subclass, visit }) {
    commit('setDocDataLoading', true)
    commit('setDocDataStatus', null)
    commit('setDocData', {})
    api.GetDocTemplateWithData({ rid: rid, subclass: subclass, visit: visit, groupId: rootState.User.group.group_id }, message => {
      if (message.data !== null) {
        const raw = message.data[0]
        const cloned = {
          ...raw,
          records: Array.isArray(raw.records)
            ? raw.records.map((r) => (r && typeof r === 'object' ? { ...r } : r))
            : raw.records
        }
        const envelope = buildLegacyTemplateDocEnvelope(cloned, { rid, visitId: visit })
        commit('setDocDataStatus', true)
        commit('setDocData', envelope)
      } else {
        commit('setDocDataStatus', false)
      }
      commit('setDocDataLoading', false)
    }, error => {
      console.log(error)
      commit('setDocDataStatus', false)
      commit('setDocDataLoading', false)
      if (error.response.status === 401) {

      }
    })
  },
  PostDoc ({ rootState, commit }, { data }) {
    commit('setDocDataLoading', true)
    commit('setDocDataPost', null)
    const payload = { ...data }
    if (Array.isArray(data.records)) {
      payload.records = data.records.map((rec) => {
        if (!rec || typeof rec !== 'object') return rec
        const next = { ...rec }
        if (Array.isArray(next.page_ele) && next.page_ele.length > 0) {
          next.page_html = buildPageHtmlFromPageEle(next.page_ele)
        }
        return next
      })
    }
    const firstRec = (Array.isArray(payload.records) && payload.records.length > 0) ? payload.records[0] : null
    const rid = (payload.rid !== undefined && payload.rid !== null && payload.rid !== '')
      ? payload.rid
      : rowPick(firstRec, 'rid', 'Rid')
    const visitId = rowPick(firstRec, 'visit_id', 'Visit_Id', 'visitId')
    const legacyPayload = buildLegacyTemplateDocEnvelope(payload, { rid, visitId })
    const lp0 = (Array.isArray(legacyPayload.records) && legacyPayload.records.length > 0) ? legacyPayload.records[0] : null
    const nowStr = sqlLikeTimestamp(new Date())
    const requestBody = {
      class_id: rowPick(legacyPayload, 'class_id', 'Class_Id', 'classId') ?? rowPick(lp0, 'class_id', 'Class_Id', 'classId'),
      class_name: rowPick(legacyPayload, 'class_name', 'Class_Name') ?? '',
      class_detail: rowPick(legacyPayload, 'class_detail', 'Class_Detail') ?? '',
      subdoc_list: [],
      rid: rowPick(legacyPayload, 'rid', 'Rid') ?? rid,
      records: []
    }
    if (Array.isArray(legacyPayload.subdoc_list) && legacyPayload.subdoc_list.length > 0) {
      const s0 = legacyPayload.subdoc_list[0]
      requestBody.subdoc_list = [{
        subclass_id: rowPick(s0, 'subclass_id', 'Subclass_Id', 'subclassId') ?? rowPick(lp0, 'subclass_id', 'Subclass_Id', 'subclassId'),
        class_id: rowPick(s0, 'class_id', 'Class_Id', 'classId') ?? requestBody.class_id,
        subclass_name: rowPick(s0, 'subclass_name', 'Subclass_Name') ?? rowPick(lp0, 'subclass_name', 'Subclass_Name') ?? '',
        subclass_detail: rowPick(s0, 'subclass_detail', 'Subclass_Detail') ?? rowPick(lp0, 'subclass_detail', 'Subclass_Detail') ?? '',
        page_template: rowPick(s0, 'page_template', 'Page_Template') ?? '',
        page_sql: rowPick(s0, 'page_sql', 'Page_Sql') ?? ''
      }]
    } else {
      requestBody.subdoc_list = [{
        subclass_id: rowPick(lp0, 'subclass_id', 'Subclass_Id', 'subclassId'),
        class_id: requestBody.class_id,
        subclass_name: rowPick(lp0, 'subclass_name', 'Subclass_Name') ?? '',
        subclass_detail: rowPick(lp0, 'subclass_detail', 'Subclass_Detail') ?? '',
        page_template: '',
        page_sql: ''
      }]
    }
    if (Array.isArray(legacyPayload.records)) {
      requestBody.records = legacyPayload.records.map((R) => {
        const next = { ...R }
        if (Array.isArray(next.page_ele) && next.page_ele.length > 0) {
          next.page_html = buildPageHtmlFromPageEle(next.page_ele)
        }
        return {
          rec_id: rowPick(next, 'rec_id', 'Rec_Id', 'recId') ?? 0,
          rid: rowPick(next, 'rid', 'Rid') ?? requestBody.rid,
          visit_id: rowPick(next, 'visit_id', 'Visit_Id', 'visitId') ?? visitId,
          subclass_id: rowPick(next, 'subclass_id', 'Subclass_Id', 'subclassId'),
          subclass_name: rowPick(next, 'subclass_name', 'Subclass_Name') ?? '',
          page_template: rowPick(next, 'page_template', 'Page_Template') ?? '',
          page_sql: rowPick(next, 'page_sql', 'Page_Sql') ?? '',
          page_ele: Array.isArray(next.page_ele) ? next.page_ele : [],
          rec_state: rowPick(next, 'rec_state', 'Rec_State') ?? 0,
          user_id: rowPick(next, 'user_id', 'User_Id') ?? 0,
          entry_dt: toSqlLikeTimestampOrFallback(rowPick(next, 'entry_dt', 'Entry_Dt'), nowStr),
          lastupdate: toSqlLikeTimestampOrFallback(rowPick(next, 'lastupdate', 'LastUpdate', 'last_update'), nowStr),
          page_html: rowPick(next, 'page_html', 'Page_Html') ?? ''
        }
      })
    }
    api.PostDoc({ data: requestBody, token: rootState.authen.token }, message => {
      if (message.data.code === 1 || message.data.code === 2) {
        commit('setDocDataPost', true)
      } else {
        commit('setDocDataPost', false)
      }
      commit('setDocDataLoading', false)
    }, error => {
      commit('setDocDataLoading', false)
      console.log(error)
      if (error.response.status === 401) {

      }
    })
  }
}

const getters = {

}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
